/**
 * Consent-gate variants for the sign-up flow.
 *
 * Used by tenants that require explicit consent before registration
 * (eXp US, eXp Commercial). Activated via `config.consentRequired` in
 * `createSignUpStore`.
 */

import type { EffectHandler } from '../../shared/flow-store';
import { signUpReducer, stripPassword } from './reducer';
import { signUpEffects } from './effects';
import type { SignUpState, SignUpAction, SignUpDeps, SignUpCreateParams } from './types';

type SignUpEffectHandler = EffectHandler<SignUpState, SignUpAction, SignUpDeps>;

/**
 * Reducer variant for the consent-required path.
 * Intercepts SUBMIT_REGISTRATION and routes to CONSENT_REQUIRED instead of REGISTERING,
 * so no signUp call is made until the user completes the consent form.
 */
export function consentGateReducer(state: SignUpState, action: SignUpAction): SignUpState {
  if (
    action.type === 'SUBMIT_REGISTRATION' &&
    (state.step === 'IDLE' || state.step === 'SIGN_IN_REQUIRED')
  ) {
    return {
      step: 'CONSENT_REQUIRED',
      email: action.params.emailAddress,
      pendingFields: stripPassword(action.params as SignUpCreateParams),
      pendingProps: action.params as SignUpCreateParams,
      requiredFields: [{ key: 'consent', label: 'Privacy & Terms' }],
      error: null,
    };
  }
  return signUpReducer(state, action);
}

/**
 * Effect map for the consent-required path.
 * SUBMIT_REGISTRATION has no effect because the reducer routes to CONSENT_REQUIRED
 * synchronously — the actual signUp call happens in submitConsentEffect.
 */
export const effectsWithoutDirectRegistration: Partial<Record<SignUpAction['type'], SignUpEffectHandler>> = {
  ...signUpEffects,
  SUBMIT_REGISTRATION: undefined,
};
